<template>
  <div
    class="c2c-picker"
    :class="{
      'c2c-picker--compact': compact,
      'c2c-picker--drag': isDragging,
      'c2c-picker--loading': loading
    }"
    @dragover.prevent="isDragging = true"
    @dragleave.prevent="isDragging = false"
    @drop.prevent="onDrop"
    @click="onClickArea"
    role="button"
    :tabindex="loading ? -1 : 0"
    @keydown.enter="onClickArea"
    @keydown.space.prevent="onClickArea"
    :aria-label="t('picker.drop_main')"
  >
    <input
      ref="inputEl"
      type="file"
      :accept="props.passthroughOnly ? 'video/webm,.webm,.mkv' : 'video/*,.mp4,.mov,.mkv,.webm,.m4v'"
      :multiple="multiple"
      class="c2c-picker-input"
      @change="onFileChange"
    />
    <div v-if="loading" class="c2c-picker-loading">
      <div class="c2c-spinner" :aria-label="t('picker.reading')"></div>
      <p>{{ t('picker.reading') }}</p>
    </div>
    <template v-else>
      <div class="c2c-picker-icon-wrap" aria-hidden="true">
        <Camera :size="20" />
      </div>
      <p class="c2c-picker-main">{{
        isTouchDevice
          ? (multiple ? t('picker.tap_main_multi') : t('picker.tap_main'))
          : (multiple ? t('picker.drop_main_multi') : t('picker.drop_main'))
      }}</p>
      <p v-if="!isTouchDevice" class="c2c-picker-sub">{{ t('picker.drop_sub') }}</p>
      <CdxButton action="progressive" weight="primary" @click.stop="onClickArea">
        {{ multiple ? t('picker.choose_button_multi') : t('picker.choose_button') }}
      </CdxButton>
    </template>
  </div>

  <AppDialog
    :visible="!!dialog"
    :title="dialog?.title || ''"
    :message="dialog?.message || ''"
    :tone="dialog?.tone || 'warn'"
    :confirm-label="t('common.ok')"
    @confirm="dialog = null"
    @cancel="dialog = null"
  />

  <!-- Soft size-warning confirmation. Single-file pending. -->
  <AppDialog
    :visible="!!pendingFile && !!pendingWarning"
    :title="t('picker.dialog_size_warning_title')"
    :message="pendingWarning"
    tone="success"
    :confirm-label="t('picker.size_warning_continue')"
    :cancel-label="t('common.cancel')"
    @confirm="confirmPending"
    @cancel="cancelPending"
  />

  <!-- Soft size-warning confirmation. Batch pending. -->
  <AppDialog
    :visible="!!pendingBatchAccept && !!pendingWarning"
    :title="t('picker.dialog_size_warning_title')"
    :message="pendingWarning"
    tone="success"
    :confirm-label="t('picker.size_warning_continue')"
    :cancel-label="t('common.cancel')"
    @confirm="confirmBatchPending"
    @cancel="cancelBatchPending"
  />
</template>

<script setup lang="ts">
import { ref } from "vue"
import { Camera } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { CdxButton } from "@wikimedia/codex"
import {
  MAX_FILE_SIZE_BYTES, HARD_MAX_FILE_SIZE_BYTES, MAX_QUEUE_FILES, formatSizeLimit
} from "@/lib/config"
import AppDialog from "@/components/AppDialog.vue"

const { t } = useI18n()

const props = withDefaults(defineProps<{
  loading: boolean
  multiple?: boolean
  /** When true, only files that can be uploaded without re-encoding (already
   *  WebM/VP9) are accepted. Used when the browser cannot encode VP9. */
  passthroughOnly?: boolean
  /** Override of MAX_FILE_SIZE_BYTES, used when the device reports limited
   *  memory. Defaults to the configured ceiling. */
  maxFileSize?: number
  /** A single row rather than a full drop panel. Used for "add more" below
   *  a queue that is already on screen, where a second 48px-padded dropzone
   *  would dominate the page. */
  compact?: boolean
  /** Hard ceiling on how many files one selection may contribute. */
  maxFiles?: number
}>(), {
  multiple: false,
  passthroughOnly: false,
  maxFileSize: MAX_FILE_SIZE_BYTES,
  compact: false,
  maxFiles: MAX_QUEUE_FILES
})

/**
 * Two emits, one for each mode. The single-file `file` emit is unchanged
 * from the previous version so existing callers keep working. Multi-file
 * callers opt in via the `multiple` prop and listen on `files` instead.
 */
const emit = defineEmits<{
  file: [File]
  files: [File[]]
}>()

const isDragging = ref(false)
const inputEl = ref<HTMLInputElement | null>(null)
const dialog = ref<{ title: string; message: string; tone: "info" | "warn" | "error" } | null>(null)

/** Touch-device detection. Used to swap copy on the picker between
 *  drag-and-drop language (desktop) and tap-language (mobile). matchMedia
 *  with (hover: none) and (pointer: coarse) is the modern reliable test;
 *  feature-detection of TouchEvent has too many false positives on
 *  touch-capable laptops. */
const isTouchDevice = (() => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false
  return window.matchMedia("(hover: none) and (pointer: coarse)").matches
})()

function showDialog(title: string, message: string, tone: "info" | "warn" | "error" = "warn") {
  dialog.value = { title, message, tone }
}

function onClickArea() { inputEl.value?.click() }

function onFileChange(e: Event) {
  const target = e.target as HTMLInputElement
  if (!target.files || target.files.length === 0) return
  handleFiles(Array.from(target.files))
  target.value = ""
}

function onDrop(e: DragEvent) {
  isDragging.value = false
  const list = Array.from(e.dataTransfer?.files || [])
  if (list.length === 0) return
  handleFiles(list)
}

/**
 * Three-state validation:
 *   { ok: true }                                  -> accept silently
 *   { ok: true, warn: "<reason>" }                -> accept after the user
 *                                                    confirms a soft warning
 *   { ok: false, reason: "<reason>" }             -> hard reject
 */
type ValidationResult =
  | { ok: true; warn?: string }
  | { ok: false; reason: string }

function validateSingle(f: File): ValidationResult {
  const isVideo = f.type.startsWith("video/") ||
    /\.(mp4|mov|mkv|webm|m4v|avi|mts|m2ts|3gp)$/i.test(f.name)
  if (!isVideo) return { ok: false, reason: t('picker.alert_not_video') }
  if (/\.(mts|m2ts)$/i.test(f.name)) {
    return { ok: false, reason: t('picker.alert_avchd', { url: 'https://handbrake.fr' }) }
  }

  // Hard ceiling - either the device-memory cap (phones under 4 GiB) or the
  // absolute HARD_MAX_FILE_SIZE_BYTES. Whichever applies, files above it are
  // rejected.
  const hardCeiling = Math.min(props.maxFileSize, HARD_MAX_FILE_SIZE_BYTES)
  if (f.size > hardCeiling) {
    return {
      ok: false,
      reason: t('picker.alert_too_large', {
        size: formatSizeLimit(f.size),
        max: formatSizeLimit(hardCeiling)
      })
    }
  }

  // The "this is a big one" band: over 1 GB but inside the 2 GB ceiling.
  //
  // This is not a warning that something is wrong, and it no longer reads
  // like one. Files this size do work - the source is streamed, and what
  // costs memory is the smaller encoded output - they just take a while and
  // the tab may stutter. So the dialog says that, in green, and the default
  // action is to carry on. It used to fire at 500 MB and recommend another
  // tool, which turned an ordinary phone recording into a scare.
  if (f.size > MAX_FILE_SIZE_BYTES && f.size <= hardCeiling) {
    return {
      ok: true,
      warn: t('picker.alert_size_warning', {
        size: formatSizeLimit(f.size),
        max: formatSizeLimit(HARD_MAX_FILE_SIZE_BYTES)
      })
    }
  }

  return { ok: true }
}

/**
 * Single-file pending-confirmation slot. When validateSingle returns a
 * warning, we hold the File here and show a two-button dialog. The
 * accept handler emits "file" to continue the normal flow; the cancel
 * handler simply discards it.
 */
const pendingFile = ref<File | null>(null)
const pendingWarning = ref<string>("")

function emitWithMaybeWarning(f: File, v: ValidationResult): void {
  if (!v.ok) {
    showDialog(t('picker.dialog_rejected_title'), v.reason, "warn")
    return
  }
  if (v.warn) {
    // Surface the warning, hold the file, wait for user confirmation.
    pendingFile.value = f
    pendingWarning.value = v.warn
    return
  }
  emit("file", f)
}

function confirmPending() {
  const f = pendingFile.value
  pendingFile.value = null
  pendingWarning.value = ""
  if (f) emit("file", f)
}

function cancelPending() {
  pendingFile.value = null
  pendingWarning.value = ""
}

function handleFiles(list: File[]) {
  // Single-file mode.
  if (!props.multiple) {
    const f = list[0]
    emitWithMaybeWarning(f, validateSingle(f))
    return
  }

  // Multi-file: validate each. Accepted files are emitted as a batch; if
  // any of them carry a soft warning, surface a single combined dialog
  // letting the user accept/cancel the WHOLE batch (we don't ask per-file
  // because that's a UX nightmare).
  const accepted: File[] = []
  const warned: { file: File; reason: string }[] = []
  const rejected: { name: string; reason: string }[] = []
  for (const f of list) {
    const v = validateSingle(f)
    if (!v.ok) { rejected.push({ name: f.name, reason: v.reason }); continue }
    if (v.warn) { warned.push({ file: f, reason: v.warn }) }
    accepted.push(f)
  }

  if (rejected.length > 0) {
    const summary = rejected.map(r => `• ${r.name}: ${r.reason}`).join("\n")
    showDialog(
      t('picker.alert_some_rejected', { n: rejected.length }),
      summary,
      "warn"
    )
  }

  if (accepted.length === 0) return

  // Commons takes 50 in a batch; so do we. Trimming here rather than
  // refusing the whole selection means dropping 60 files still gets 50 of
  // them queued, with the overflow named rather than silently dropped.
  if (accepted.length > props.maxFiles) {
    const dropped = accepted.length - props.maxFiles
    accepted.length = props.maxFiles
    showDialog(
      t('picker.limit_title'),
      t('picker.limit_trimmed', { n: dropped, limit: MAX_QUEUE_FILES }),
      "warn"
    )
  }

  const finalize = () => {
    if (accepted.length === 1) emit("file", accepted[0])
    else emit("files", accepted)
  }

  if (warned.length > 0) {
    const head = t('picker.alert_batch_size_warning', {
      n: warned.length,
      max: formatSizeLimit(HARD_MAX_FILE_SIZE_BYTES)
    })
    const lines = warned.map(w => `• ${w.file.name}: ${w.reason}`).join("\n")
    pendingFile.value = null // batch mode - no single pending file
    pendingWarning.value = head + "\n\n" + lines
    pendingBatchAccept.value = finalize
    return
  }

  finalize()
}

const pendingBatchAccept = ref<(() => void) | null>(null)

function confirmBatchPending() {
  const fn = pendingBatchAccept.value
  pendingBatchAccept.value = null
  pendingWarning.value = ""
  if (fn) fn()
}

function cancelBatchPending() {
  pendingBatchAccept.value = null
  pendingWarning.value = ""
}
</script>

<style scoped>
/* The compact variant is the same control in one row: used beneath a queue
   that is already on screen, where a second full-height dropzone would
   outweigh the list it is attached to. */
.c2c-picker--compact {
  padding: 14px 16px;
  display: flex; flex-wrap: wrap; align-items: center;
  justify-content: center; gap: 10px 14px;
  text-align: start;
}
.c2c-picker--compact .c2c-picker-icon-wrap,
.c2c-picker--compact .c2c-picker-sub { display: none; }
.c2c-picker--compact .c2c-picker-main { margin: 0; font-size: 14px; }

.c2c-picker {
  border: 2px dashed #a2a9b1;
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  background: #f8f9fa;
  transition: border-color 0.15s, background 0.15s;
}
.c2c-picker:hover, .c2c-picker:focus-visible {
  border-color: #3366cc;
  background: #eaf3ff;
  outline: none;
}
.c2c-picker--drag { border-color: #3366cc; background: #eaf3ff; }
.c2c-picker--loading { cursor: wait; }
.c2c-picker-input { display: none; }
.c2c-picker-icon-wrap {
  display: inline-flex; align-items: center; justify-content: center;
  width: 48px; height: 48px; border-radius: 50%;
  background: white; margin-bottom: 16px;
  color: #54595d; border: 1px solid #eaecf0;
}
.c2c-picker-icon-wrap :deep(svg) { width: 28px; height: 28px; }
.c2c-picker-main { font-size: 16px; font-weight: 600; margin: 0 0 6px 0; }
.c2c-picker-sub { font-size: 13px; color: #54595d; margin: 0 0 16px 0; }
.c2c-spinner {
  width: 32px; height: 32px;
  border: 3px solid #c8ccd1;
  border-top-color: #3366cc;
  border-radius: 50%;
  margin: 0 auto 12px auto;
  animation: c2c-spin 0.8s linear infinite;
}
@keyframes c2c-spin { to { transform: rotate(360deg); } }
@media (max-width: 540px) { .c2c-picker { padding: 32px 16px; } }
</style>
